import { lazy, useCallback, useEffect, useMemo, useLayoutEffect, useRef, useState, useSyncExternalStore, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { WorkDataStore } from "./workDataStore";
import { WorkDataProvider } from "./WorkDataContext";
import { ApiError, api, readKioskDeviceToken, readSession, writeKioskDeviceToken, writeSession } from "./api";
import { AppNavigation } from "./components/AppNavigation";
import { PageState } from "./components/ui/PageState";
import { Button } from "./components/ui/Button";
import { DesignSystem, usesDesignSystem } from "./components/ui/DesignSystem";
import { SettingsDialog } from "./components/SettingsDialog";
import type { TabletTab } from "./components/FieldTablet";
import { LoginScreen } from "./components/LoginScreen";
import { PageLoadBoundary } from "./components/PageLoadBoundary";
import type { AuthSession, BootstrapResponse, FieldBootstrapResponse, FinishTabletBootstrapResponse, WorkPlanItem } from "./types";
import { locationChangeEvent, requestPageLeave, type NavigationOutcome } from "./pageNavigation";
import { canAccessView, type AppView } from "./routeAccess";
import { WorkpieceCommandProvider } from "./components/WorkpieceCommandProvider";

const WorkManagement = lazy(() => import("./components/WorkManagement").then(module => ({ default: module.WorkManagement })));
const FieldTablet = lazy(() => import("./components/FieldTablet").then(module => ({ default: module.FieldTablet })));
const Tablet = lazy(() => import("./components/FinishTablet").then(module => ({ default: module.Tablet })));
const SettlementPage = lazy(() => import("./components/SettlementPage").then(module => ({ default: module.SettlementPage })));
const OpsManagement = lazy(() => import("./components/OpsManagement").then(module => ({ default: module.OpsManagement })));
const ProjectManagement = lazy(() => import("./components/ProjectManagement").then(module => ({ default: module.ProjectManagement })));
const ProjectWorkspace = lazy(() => import("./components/ProjectWorkspace").then(module => ({ default: module.ProjectWorkspace })));
const SettingsPage = lazy(() => import("./components/SettingsPage").then(module => ({ default: module.SettingsPage })));
const PlanningReferencePage = lazy(() => import("./components/PlanningReferencePage").then(module => ({ default: module.PlanningReferencePage })));
const UserManagement = lazy(() => import("./components/UserManagement").then(module => ({ default: module.UserManagement })));
const ActivityHistoryPage = lazy(() => import("./components/ActivityHistoryPage").then(module => ({ default: module.ActivityHistoryPage })));
const LegacyWorkRecordsPage = lazy(() => import("./components/LegacyWorkRecordsPage").then(module => ({ default: module.LegacyWorkRecordsPage })));
const AnnouncementPage = lazy(() => import("./components/AnnouncementPage").then(module => ({ default: module.AnnouncementPage })));
const WorkLabelPage = lazy(() => import("./components/WorkLabelPage").then(module => ({ default: module.WorkLabelPage })));
const WorkpiecePage = lazy(() => import("./components/WorkpiecePage").then(module => ({ default: module.WorkpiecePage })));
const RackStatusPage = lazy(() => import("./components/RackStatusPage").then(module => ({ default: module.RackStatusPage })));
const RackTablet = lazy(() => import("./components/RackTablet").then(module => ({ default: module.RackTablet })));
const RackOverviewPage = lazy(() => import("./components/RackOverviewPage").then(module => ({ default: module.RackOverviewPage })));

type View = AppView;
const demoMode = import.meta.env.VITE_TEDDIFY_DEMO === "true";

interface AppRoute {
  path: string;
  view: View;
  title: string;
  tabletTab?: TabletTab;
}

const routes: AppRoute[] = [
  { path: "/", view: "planner", title: "작업현황" },
  { path: "/planner", view: "planner", title: "작업현황" },
  { path: "/work", view: "work", title: "프로젝트" },
  { path: "/projects", view: "projects", title: "프로젝트" },
  { path: "/studio-admin", view: "workers", title: "작업자" },
  { path: "/settlement", view: "settlement", title: "정산" },
  { path: "/ops", view: "ops", title: "운영" },
  { path: "/settings", view: "account", title: "설정" },
  { path: "/planning-data", view: "planning-data", title: "플래닝 기준 정보" },
  { path: "/admin/users", view: "users", title: "사용자 관리" },
  { path: "/admin/activity-history", view: "activity-history", title: "작업 이력" },
  { path: "/admin/legacy-work-records", view: "legacy-work-records", title: "기존 서버 작업 기록" },
  { path: "/settings/aligo", view: "aligo", title: "설정" },
  { path: "/settings/printer", view: "printer", title: "설정" },
  { path: "/racks/settings", view: "rack-overview", title: "랙 현황" },
  { path: "/announcements", view: "announcements", title: "공지메시지" },
  { path: "/field", view: "field", title: "완성작업 입력", tabletTab: "my-work" },
  { path: "/field/inspection", view: "field", title: "검수 대기", tabletTab: "inspection" },
  { path: "/tablet", view: "tablet", title: "태블릿 작업" },
  { path: "/racks", view: "rack-overview", title: "랙 현황" },
  { path: "/rack-tablet", view: "rack-tablet", title: "랙 태블릿" }
];

type TabletStage = "finish" | "sewing";
const tabletStageStorageKey = "teddify-tablet-stage";
const initialTabletStage = (): TabletStage => {
  if (normalizePath(window.location.pathname) === "/sewing-tablet") return "sewing";
  if (normalizePath(window.location.pathname) === "/finish-tablet") return "finish";
  return localStorage.getItem(tabletStageStorageKey) === "sewing" ? "sewing" : "finish";
};

const normalizePath = (path: string) => path !== "/" ? path.replace(/\/+$/, "") : path;
const canonicalSearch = (path: string, search: string) => {
  if (normalizePath(path) !== "/layout/settings") return search;
  const params = new URLSearchParams(search);
  params.set("structure", "1");
  return `?${params}`;
};
const routeFromPath = (path: string, search = "") => {
  const normalized = normalizePath(path);
  if (normalized === "/account" || normalized === "/settings/account") return routes.find(route => route.path === "/settings")!;
  if (normalized === "/admin/aligo") return routes.find(route => route.path === "/settings/aligo")!;
  if (normalized === "/settings/workpiece-flow" || normalized === "/layout" || normalized === "/layout/settings") return routes.find(route => route.path === "/racks/settings")!;
  const legacyIssuanceId = normalized === "/ops" ? new URLSearchParams(search).get("issuance") : null;
  if (legacyIssuanceId && /^[0-9a-f-]{36}$/i.test(legacyIssuanceId)) return { path: `/work-label/${legacyIssuanceId}`, view: "work-label", title: "작업 확인" } satisfies AppRoute;
  if (/^\/work-label\/[0-9a-f-]{36}$/i.test(normalized)) return { path: normalized, view: "work-label", title: "작업 확인" } satisfies AppRoute;
  if (/^\/workpiece\/[0-9a-f-]{36}$/i.test(normalized)) return { path: normalized, view: "workpiece", title: "작업물 확인" } satisfies AppRoute;
  if (/^\/racks\/[0-9a-f-]{36}$/i.test(normalized)) return { path: normalized, view: "rack-status", title: "랙 현황" } satisfies AppRoute;
  if (/^\/rack-tablet\/[0-9a-f-]{36}$/i.test(normalized)) return { path: normalized, view: "rack-tablet", title: "랙 작업물" } satisfies AppRoute;
  if (normalized === "/finish-tablet" || normalized === "/sewing-tablet") return routes.find((route) => route.path === "/tablet")!;
  if (normalized === "/planner/planning" || normalized === "/planner/a" || normalized === "/planner/settings" || normalized === "/planner/efficiency") return routes.find((route) => route.path === "/planner")!;
  if (normalized === "/field/work") return routes.find((route) => route.path === "/field")!;
  if (normalized === "/studio-admin/messages") return routes.find((route) => route.path === "/announcements")!;
  if (normalized === "/workers" || normalized.startsWith("/studio-admin/")) return routes.find((route) => route.path === "/studio-admin")!;
  if (normalized === "/settlements" || normalized === "/settlements/piecework" || normalized === "/settlements/processing" || normalized === "/settlements/part-time" || normalized === "/settlements/contracts" || normalized === "/settlements/all") return routes.find((route) => route.path === "/settlement")!;
  return routes.find((route) => route.path === normalized) ?? routes[0];
};

function AppContent() {
  const [route, setRoute] = useState<AppRoute>(() => routeFromPath(window.location.pathname, window.location.search));
  const [session, setSession] = useState<AuthSession | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [workData] = useState(() => new WorkDataStore());
  const { data } = useSyncExternalStore(workData.subscribe, workData.getSnapshot, workData.getSnapshot);
  const setData = useCallback((value: null) => { if (value === null) workData.clear(); }, [workData]);
  const [fieldData, setFieldData] = useState<FieldBootstrapResponse | null>(null);
  const [tabletData, setTabletData] = useState<FinishTabletBootstrapResponse | null>(null);
  const [tabletStage, setTabletStage] = useState<TabletStage>(initialTabletStage);
  const [loadError, setLoadError] = useState<string | null>(null);
  const refreshSequence = useRef(0);
  const loggingOut = useRef(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [focusPlanItem, setFocusPlanItem] = useState<WorkPlanItem | null>(null);
  const [clientOutdated, setClientOutdated] = useState(false);
  const [tabletDeviceRequired, setTabletDeviceRequired] = useState(false);
  const refreshScope = useRef({ route, session, tabletStage });
  refreshScope.current = { route, session, tabletStage };

  const refresh = useCallback(async (preserveScreen = false) => {
    const sequence = ++refreshSequence.current;
    const attempt = async (remaining: number): Promise<void> => {
      try {
        const { route, session, tabletStage } = refreshScope.current;
        const readToken = workData.readToken();
        const currentUser = session?.user;
        const workerSession = currentUser?.role === "worker";
        const tabletRoute = route.view === "tablet";
        if (route.view === "rack-tablet") return;
        const includeInactiveWorkers = route.view === "workers" && Boolean(currentUser && canAccessView(currentUser, "workers"));
        const next = tabletRoute ? await api.tabletBootstrap(tabletStage) : workerSession ? await api.fieldBootstrap() : await api.bootstrap({ includeInactiveWorkers });
        if (sequence !== refreshSequence.current) return;
        if (tabletRoute) setTabletData(next as FinishTabletBootstrapResponse);
        else if (workerSession) setFieldData(next as FieldBootstrapResponse);
        else workData.receive(next as BootstrapResponse, readToken);
        setLoadError(null);
      } catch (error) {
        if (sequence !== refreshSequence.current) return;
        if (route.view === "tablet" && error instanceof ApiError
            && ["kiosk_device_token_required", "kiosk_device_token_invalid", "kiosk_worker_mode_disabled"].includes(error.code ?? "")) {
          setTabletDeviceRequired(true);
          setAuthError(error.message);
          setLoadError(null);
          return;
        }
        if (preserveScreen) throw error;
        // A brief connection hiccup shouldn't flash the hard error screen; retry quietly first.
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          if (sequence !== refreshSequence.current) return;
          return attempt(remaining - 1);
        }
        setLoadError(error instanceof Error ? error.message : "업무 데이터를 불러오지 못했습니다.");
      }
    };
    await attempt(2);
  }, [route.view, session?.user, tabletStage, workData]);
  // Repeated planner reads must not unmount an in-progress edit on a connection error.
  const refreshPlanner = useCallback(() => refresh(true), [refresh]);

  const navigationIndex = useRef(Number(window.history.state?.teddifyNavigationIndex ?? 0));
  const restoringNavigation = useRef(false);
  const navigate = useCallback((path: string, replace = false) => {
    if (!replace && loggingOut.current) return false;
    const target = new URL(path, window.location.origin);
    const next = routeFromPath(target.pathname, target.search);
    const nextLocation = `${next.path}${canonicalSearch(target.pathname, target.search)}${target.hash}`;
    const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentLocation !== nextLocation) {
      if (!replace && !requestPageLeave()) return false;
      if (!replace) navigationIndex.current += 1;
      window.history[replace ? "replaceState" : "pushState"]({ ...window.history.state, teddifyNavigationIndex: navigationIndex.current }, "", nextLocation);
    }
    setRoute(next);
    window.dispatchEvent(new Event(locationChangeEvent));
    return true;
  }, []);

  /* 링크가 아니라 단추에서도 부른다(프로필 아바타). 그때는 넘길 이벤트가 없다 — 새 탭으로
     여는 조합키도, 막을 기본 동작도 없으므로 바로 옮긴다. */
  const followLink = (event: ReactMouseEvent<HTMLAnchorElement> | undefined, path: string): NavigationOutcome => {
    if (loggingOut.current) { event?.preventDefault(); return "blocked"; }
    if (!event) return navigate(path) ? "accepted" : "blocked";
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return "native";
    event.preventDefault();
    return navigate(path) ? "accepted" : "blocked";
  };

  useEffect(() => {
    const canonical = routeFromPath(window.location.pathname, window.location.search);
    window.history.replaceState({ ...window.history.state, teddifyNavigationIndex: navigationIndex.current }, "", window.location.pathname !== canonical.path ? `${canonical.path}${canonicalSearch(window.location.pathname, window.location.search)}${window.location.hash}` : window.location.href);
    const handlePopState = (event: PopStateEvent) => {
      if (restoringNavigation.current) { restoringNavigation.current = false; return; }
      const nextIndex = Number(event.state?.teddifyNavigationIndex ?? 0);
      if (nextIndex !== navigationIndex.current && (loggingOut.current || !requestPageLeave())) {
        restoringNavigation.current = true;
        window.history.go(navigationIndex.current - nextIndex);
        return;
      }
      navigationIndex.current = nextIndex;
      setRoute(routeFromPath(window.location.pathname, window.location.search));
      window.dispatchEvent(new Event(locationChangeEvent));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const title = route.view === "tablet" ? `${tabletStage === "finish" ? "완성" : "재봉"} 작업` : route.title;
    document.title = `Factory OS${demoMode ? " Demo" : ""}`;
  }, [route.title, route.view, tabletStage]);

  useEffect(() => {
    localStorage.setItem(tabletStageStorageKey, tabletStage);
  }, [tabletStage]);

  useEffect(() => {
    const initializeSession = async () => {
      if (route.view === "tablet" || route.view === "rack-tablet") return;
      const saved = readSession();
      if (saved) {
        try {
          const { user } = await api.me();
          const restored = { ...saved, user };
          writeSession(restored);
          setSession(restored);
          return;
        } catch {
          writeSession(null);
        }
      }
      if (import.meta.env.DEV || demoMode) {
        try {
          const developmentSession = await api.enterDevelopmentMode();
          writeSession(developmentSession);
          setSession(developmentSession);
        } catch {
          // A non-development backend keeps the normal login screen available.
        }
      }
    };
    void initializeSession().finally(() => setAuthReady(true));
  }, []);

  useEffect(() => {
    if (!session && route.view !== "tablet") return;
    if (route.view === "rack-tablet") return;
    if (route.view === "work-label" || route.view === "workpiece") return;
    void refresh();
  }, [session, route.view, refresh]);

  useEffect(() => {
    const expired = () => {
      refreshSequence.current++; writeSession(null); setSession(null); setData(null); setFieldData(null); setTabletData(null); setLoadError(null);
      if (route.view === "tablet") {
        void refresh();
        return;
      }
      setAuthError("로그인 세션이 종료되었습니다. 다시 로그인해 주세요.");
      if (route.view !== "work-label" && route.view !== "workpiece") navigate("/", true);
    };
    window.addEventListener("teddify-auth-expired", expired);
    return () => window.removeEventListener("teddify-auth-expired", expired);
  }, [navigate, refresh, route.view]);

  useEffect(() => {
    const outdated = () => setClientOutdated(true);
    window.addEventListener("teddify-client-outdated", outdated);
    return () => window.removeEventListener("teddify-client-outdated", outdated);
  }, []);

  useEffect(() => {
    const requireKioskAuth = (event: Event) => {
      const detail = (event as CustomEvent<{ code?: string; message?: string }>).detail;
      if (detail?.code === "kiosk_device_token_invalid") writeKioskDeviceToken(null);
      setTabletData(null);
      setTabletDeviceRequired(true);
      setAuthError(detail?.message ?? "태블릿 장치를 다시 인증해 주세요.");
    };
    window.addEventListener("teddify-kiosk-auth-required", requireKioskAuth);
    return () => window.removeEventListener("teddify-kiosk-auth-required", requireKioskAuth);
  }, []);

  useEffect(() => {
    if (session?.user.role === "worker" && route.view !== "field" && route.view !== "tablet" && route.view !== "rack-tablet") navigate("/field", true);
  }, [navigate, route.view, session?.user.role]);

  const enter = (next: AuthSession) => {
    writeSession(next);
    setSession(next);
    setAuthError(null);
  };

  const login = async (username: string, password: string) => {
    if (!username || !password) {
      setAuthError("아이디와 비밀번호를 입력하세요.");
      return;
    }
    setAuthBusy(true);
    setAuthError(null);
    try {
      enter(await api.login(username, password));
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "로그인 중 오류가 발생했습니다.");
    } finally {
      setAuthBusy(false);
    }
  };

  const enterWorkerMode = async (deviceToken?: string) => {
    setAuthBusy(true);
    setAuthError(null);
    try {
      const supplied = (deviceToken ?? readKioskDeviceToken()) || undefined;
      enter(await api.enterWorkerMode(supplied));
      if (deviceToken) writeKioskDeviceToken(deviceToken);
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.code === "kiosk_device_token_invalid") writeKioskDeviceToken(null);
      setAuthError(error instanceof Error ? error.message : "현장 화면을 열지 못했습니다.");
      return false;
    } finally {
      setAuthBusy(false);
    }
  };

  const enterTabletDevice = async (deviceToken?: string) => {
    setAuthBusy(true);
    setAuthError(null);
    if (deviceToken) writeKioskDeviceToken(deviceToken);
    try {
      if (route.view === "rack-tablet") {
        if (route.path === "/rack-tablet") await api.rackTabletList();
        else await api.rackTabletStatus(route.path.slice("/rack-tablet/".length));
      }
      else setTabletData(await api.tabletBootstrap(tabletStage));
      setTabletDeviceRequired(false);
      return true;
    } catch (error) {
      if (error instanceof ApiError && error.code === "kiosk_device_token_invalid") writeKioskDeviceToken(null);
      setAuthError(error instanceof Error ? error.message : "태블릿 장치를 인증하지 못했습니다.");
      return false;
    } finally {
      setAuthBusy(false);
    }
  };

  const signup = async (input: { username: string; nickname: string; password: string }) => {
    setAuthBusy(true); setAuthError(null);
    try { await api.signup(input); return true; }
    catch (error) { setAuthError(error instanceof Error ? error.message : "가입 신청 중 오류가 발생했습니다."); return false; }
    finally { setAuthBusy(false); }
  };

  const clearSession = () => {
    refreshSequence.current++; writeSession(null); setSession(null); setData(null); setFieldData(null); setTabletData(null); setLoadError(null); navigate("/", true);
  };

  const logout = async () => {
    if (loggingOut.current || !requestPageLeave()) return false;
    loggingOut.current = true; setLogoutBusy(true);
    try {
      try { await api.logout(); } catch { /* expired sessions are cleared locally */ }
      // Invalidate old reads before clearing this session's screen/data.
      refreshSequence.current++;
      writeSession(null); setData(null); setFieldData(null); setTabletData(null); setLoadError(null);
      navigate("/", true);
      let developmentSession: AuthSession | null = null;
      if (import.meta.env.DEV || demoMode) {
        try { developmentSession = await api.enterDevelopmentMode(); }
        catch { /* Non-development backends remain on the login screen. */ }
      }
      if (developmentSession) enter(developmentSession);
      else setSession(null);
      return true;
    } finally { loggingOut.current = false; setLogoutBusy(false); }
  };

  const openWorkRecord = (issuanceId: string) => navigate(`/work-label/${issuanceId}`);

  const pageView = session?.user.role === "worker" && route.view !== "tablet" && route.view !== "rack-tablet" ? "field" : route.view;
  const designSystem = usesDesignSystem(route.path, Boolean(session));
  /* 고른 밝기는 이 기계에 남는다 — 사람마다 쓰는 자리의 조명이 다르고, 그건 계정이 아니라
     그 자리의 사정이라 서버에 보내지 않는다. 관리 화면은 전부터 밝은 쪽 하나뿐이다.
     기본값은 밝은 쪽이다(2026-10-16). 판의 모양을 claude.ai/code 에서 재서 옮기기로 했고,
     저쪽에 어두운 판이 따로 없어 기준으로 삼을 값이 밝은 쪽에만 있다. 어두운 판은 그대로
     남아 있다. 저장 이름도 같이 갈았다(-2) — 기본값만 바꾸면 전에 어두운 쪽을 고른 기계는
     그 값이 남아 있어 아무것도 바뀌지 않는다. 쓰던 사람은 한 번만 다시 고르면 된다. */
  /* 2026-10-19: 고르개가 Claude 와 같은 세 칸(시스템·라이트·다크)이 되면서 상태도 셋이다.
     "시스템"은 저장되는 값이고, 실제로 칠할 색은 그때그때 OS 설정을 물어 정한다 — 저장값과
     적용값을 한 변수로 쓰면 OS 를 바꿨을 때 따라가지 못한다. */
  /* 2026-10-19 밤 지윤: 기본은 라이트로 고정 — 기기(OS) 설정을 따라가지 않는다. 전에 시스템·다크를 골라 둔
     기계도 한 번은 라이트로 시작하도록 저장 이름을 -3 으로 갈았다. 설정 > 일반에서 다시 고르면 그 값이 남는다. */
  const [theme, setTheme] = useState<"system" | "light" | "dark">(() => {
    try {
      const saved = localStorage.getItem("workspace-theme-mode-3");
      return saved === "dark" || saved === "light" || saved === "system" ? saved : "light";
    } catch { return "light"; }
  });
  const [systemDark, setSystemDark] = useState(() => {
    try { return window.matchMedia("(prefers-color-scheme: dark)").matches; } catch { return false; }
  });
  useLayoutEffect(() => {
    let media: MediaQueryList;
    try { media = window.matchMedia("(prefers-color-scheme: dark)"); } catch { return; }
    const listen = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener("change", listen);
    return () => media.removeEventListener("change", listen);
  }, []);
  const appliedTheme = theme === "system" ? (systemDark ? "dark" : "light") : theme;
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.workspaceTheme = designSystem ? "management" : "planning";
    root.dataset.theme = designSystem ? "light" : appliedTheme;
    root.dataset.accent = "neutral";
    try { localStorage.setItem("workspace-theme-mode-3", theme); } catch { /* 사생활 보호 창 */ }
  }, [designSystem, appliedTheme, theme]);
  const standalone = (children: ReactNode) => <DesignSystem enabled={designSystem} planningBaseline={!designSystem} scope>{children}</DesignSystem>;
  const canEdit = session?.user.role === "admin" || session?.user.role === "editor";
  const content = () => {
    if (session && !canAccessView(session.user, pageView)) {
      return <PageState title="접근 권한이 없습니다" description={`${route.title} 페이지를 사용할 권한이 없습니다.`} />;
    }
    if (pageView === "planning-data") return <PlanningReferencePage onNavigate={navigate} />;
    if (pageView === "account" || pageView === "aligo" || pageView === "printer") return <SettingsPage tab={pageView} isAdmin={session!.user.role === "admin"} username={session!.user.username} name={session!.user.name} onPasswordChanged={clearSession} onNavigate={navigate} data={data} refresh={refreshPlanner} canViewPlanning={canAccessView(session!.user, "planning-data")} canEditPlanning={canEdit && session!.user.writeTabs.includes("planner")} />;
    if (pageView === "rack-tablet") return route.path === "/rack-tablet"
      ? <RackTablet />
      : <RackStatusPage rackId={route.path.slice("/rack-tablet/".length)} tablet canEdit onNavigate={navigate} />;
    if (pageView === "rack-status") return <RackStatusPage rackId={route.path.slice("/racks/".length)} tablet={false} canEdit={canEdit} onNavigate={navigate} />;
    if (pageView === "rack-overview") return <RackOverviewPage onNavigate={navigate} management={route.path === "/racks/settings"} canEditLayout={canEdit && session!.user.writeTabs.includes("operations")} isAdmin={session!.user.role === "admin"} />;
    if (pageView === "users") return <UserManagement />;
    if (pageView === "activity-history") return <ActivityHistoryPage />;
    if (pageView === "legacy-work-records") return <LegacyWorkRecordsPage />;
    if (pageView === "work-label") return <WorkLabelPage issuanceId={route.path.slice("/work-label/".length)} canEdit={canEdit} />;
    if (pageView === "workpiece") return <WorkpiecePage token={route.path.slice("/workpiece/".length)} canEdit={canEdit} />;
    // A failed load is usually a server that is coming back, so it reads as the app's own
    // loading state and keeps retrying, rather than a full-page error wall.
    if (loadError) return <PageState busy title={loadError} />;
    if (pageView === "tablet") {
      if (!tabletData) return <PageState busy />;
      return <Tablet data={tabletData} stage={tabletStage} refresh={refreshPlanner} onStageChange={(nextStage) => {
        refreshSequence.current++;
        setTabletData(null);
        setLoadError(null);
        setTabletStage(nextStage);
      }} />;
    }
    if (pageView === "field") {
      const tabletData = session?.user.role === "worker" ? fieldData : data;
      if (!tabletData) return <PageState busy />;
      return <FieldTablet data={tabletData} refresh={refreshPlanner} tab={route.tabletTab ?? "my-work"} onTabChange={(tab) => navigate(tab === "inspection" ? "/field/inspection" : "/field")} />;
    }
    if (!data) return <PageState busy />;
    if (pageView === "work") return <ProjectWorkspace projects={data.projects} issuances={data.issuances} workers={data.workers} refresh={refreshPlanner} canEdit={canEdit} navigate={navigate} />;
    if (pageView === "announcements") return <AnnouncementPage data={data} refresh={refreshPlanner} canManageSettings={session?.user.role === "admin"} onOpenSettings={() => navigate("/settings/aligo")} />;
    if (pageView === "planner" || pageView === "workers") return <WorkManagement section={pageView} data={data} refresh={refresh} refreshPreserving={refreshPlanner} sourceIssuanceId={new URLSearchParams(window.location.search).get("issuance")} focusPlanItem={focusPlanItem} onFocusPlanItemHandled={() => setFocusPlanItem(null)} onOpenWorkRecord={openWorkRecord} actorName={session?.user.name ?? "실무 관리자"} canEdit={canEdit} />;
    if (pageView === "settlement") return <SettlementPage data={data} actorName={session?.user.name ?? "실무 관리자"} canEdit={canEdit} />;
    if (pageView === "projects") return <ProjectManagement data={data} refresh={refreshPlanner} canEdit={canEdit} />;
    if (pageView === "ops") return <OpsManagement data={data} refresh={refreshPlanner} actorName={session?.user.name ?? "실무 관리자"} canEdit={canEdit} />;
    return <PageState title="페이지를 찾을 수 없습니다" />;
  };

  if (!authReady) return standalone(<PageState title="로그인 상태 확인 중" busy />);
  if (clientOutdated) return standalone(<PageState title="새 버전이 배포되었습니다" description="현재 화면에서는 더 이상 작업을 저장할 수 없습니다." retry={() => window.location.reload()} retryLabel="새 버전 불러오기" />);
  if ((pageView === "tablet" || pageView === "rack-tablet") && tabletDeviceRequired) return standalone(<LoginScreen deviceOnly error={authError} busy={authBusy} onLogin={login} onSignup={signup} onWorkerMode={enterTabletDevice} onClearError={() => setAuthError(null)} />);
  if (pageView === "tablet" || pageView === "rack-tablet") return standalone(<PageLoadBoundary key={route.path} title={route.title}>{content()}</PageLoadBoundary>);
  if (!session) return standalone(<LoginScreen error={authError} busy={authBusy} onLogin={login} onSignup={signup} onWorkerMode={enterWorkerMode} onClearError={() => setAuthError(null)} />);

  if (pageView === "work-label" || pageView === "workpiece") return standalone(<PageLoadBoundary key={route.path} title={route.title}>{content()}</PageLoadBoundary>);

  if (session.user.role === "worker") {
    return standalone(<div className="worker-mode-shell"><header className="app-global-header worker-mode-header"><div className="app-global-inner"><span>현장 작업</span><Button variant="utility" size="compact" busy={logoutBusy} onClick={() => void logout()}>{logoutBusy ? "전환 중" : "로그인 화면"}</Button></div></header><PageLoadBoundary key={pageView} title="현장 작업">{content()}</PageLoadBoundary></div>);
  }

  /* 플래너는 화면 높이에 맞춘 판 구성을 유지하되, 다른 화면과 같은 내비게이션을 보여준다. */
  const bare = pageView === "planner";
  return (
    <WorkDataProvider store={workData}><DesignSystem enabled={designSystem} planningBaseline={!designSystem}><div className={`app-shell${designSystem ? " ui-next" : ""}${bare ? " app-shell--bare" : ""}`}>
      {/* 프로필을 누르면 Claude 처럼 설정 창이 뜬다 — 계정·휴무일·작업자·성과·사용자를 한 창에 모은다 (2026-10-18). */}
      {accountOpen && <DesignSystem scope><SettingsDialog username={session.user.username} name={session.user.name}
        role={{ admin: "관리자", editor: "편집자", viewer: "조회자", worker: "현장 작업자" }[session.user.role]}
        isAdmin={session.user.role === "admin"} canEdit={canEdit} data={data ?? undefined} refresh={refreshPlanner}
        canViewPlannerRules={session.user.role !== "viewer" && canAccessView(session.user, "planner")} canEditPlannerRules={canEdit && session.user.writeTabs.includes("planner")}
        theme={theme} onThemeChange={setTheme}
        onPasswordChanged={clearSession} onClose={() => setAccountOpen(false)} onLogout={logout}
        onActivityHistory={() => { if (navigate("/admin/activity-history")) setAccountOpen(false); }} /></DesignSystem>}
      <AppNavigation accountOnly={bare} activeView={pageView} name={session.user.name} role={{ admin: "관리자", editor: "편집자", viewer: "조회자", worker: "현장 작업자" }[session.user.role]} isAdmin={session.user.role === "admin"} visibleViews={new Set(routes.filter(candidate => canAccessView(session.user, candidate.view)).map(candidate => candidate.view))} busy={logoutBusy} onFollow={followLink} onLogout={logout}
        onOpenAccount={() => setAccountOpen(true)}
        /* 알림 종은 잠시 걷는다. 지금 세는 것이 무엇인지(밀린 작업·끝난 일·경고가 한 숫자에
           섞여 있다) 화면에서 말하지 못하는데, 머리줄에서 가장 먼저 눈에 띄는 숫자였다.
           세는 기준이 정해지면 PlannerNotifications를 그대로 되살리면 된다. */ />
      <div className="app-content" id="main-content" tabIndex={-1}><PageLoadBoundary key={pageView === "account" || pageView === "aligo" || pageView === "printer" ? "settings" : pageView} title={route.title}>{content()}</PageLoadBoundary></div>
      {/* Holidays live in AccountDialog; planner controls belong to their own
          project/worker entries, not a second modal over the board. */}
    </div></DesignSystem></WorkDataProvider>
  );
}

export function App() { return <WorkpieceCommandProvider><AppContent /></WorkpieceCommandProvider>; }
